/** Normalize values from MUI DatePicker / strings for `CreateRazaEventDto.date` (YYYY-MM-DD). */
export function toApiDateOnly(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (s.includes("T")) return s.slice(0, 10);
  }
  return "";
}

function optionalUuidOrNull(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

/** Apply male/female need flags and clear ids when a need flag is off. */
function applyNeedSadaratToPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const needSadaratMale = payload.needSadaratMale !== false;
  const needSadaratFemale = payload.needSadaratFemale === true;
  payload.needSadaratMale = needSadaratMale;
  payload.needSadaratFemale = needSadaratFemale;

  if (!needSadaratMale) {
    payload.sadaratMaleId = null;
  } else {
    payload.sadaratMaleId = optionalUuidOrNull(payload.sadaratMaleId);
  }

  if (!needSadaratFemale) {
    payload.sadaratFemaleId = null;
  } else {
    payload.sadaratFemaleId = optionalUuidOrNull(payload.sadaratFemaleId);
  }

  delete payload.needSadarat;
  delete payload.sadaratId;
  delete payload.sadarat;
  delete payload.sadaratMale;
  delete payload.sadaratFemale;
  return payload;
}

/** Drop server-managed / nested fields from PUT body for raza-backed events. */
export function stripReadOnlyRazaEventFields(
  data: Record<string, unknown>
): Record<string, unknown> {
  const rest = { ...data };
  delete rest.id;
  delete rest.tenantId;
  delete rest.googleCalendarEventId;
  delete rest.createdAt;
  delete rest.updatedAt;
  delete rest.ohbatType;
  delete rest.razaRequestId;
  delete rest.razaRequest;
  delete rest.sadarat;
  delete rest.sadaratMale;
  delete rest.sadaratFemale;
  delete rest.hallBookings;
  delete rest.lagatReceipt;
  delete rest.attendanceCount;
  delete rest.googleCalendarLinked;
  delete rest.khidmatguzar;
  delete rest.zakereen;
  delete rest.hostSector;
  delete rest.hostSubSector;
  delete rest.purposeName;
  rest.date = toApiDateOnly(rest.date);
  return applyNeedSadaratToPayload(rest);
}

/** POST body for Ohbat event create (raza-events list create). */
export function buildOhbatEventCreatePayload(
  data: Record<string, unknown>
): Record<string, unknown> {
  const date = toApiDateOnly(data.date);
  const payload: Record<string, unknown> = {
    ohbatType: data.ohbatType,
    date,
    startTime: String(data.startTime ?? "09:00").trim(),
    hostItsNo: String(data.hostItsNo ?? "").trim(),
  };
  const hn = typeof data.hostName === "string" ? data.hostName.trim() : "";
  if (hn) payload.hostName = hn;
  payload.needSadaratMale = data.needSadaratMale !== false;
  payload.needSadaratFemale = data.needSadaratFemale === true;
  payload.sadaratMaleId = payload.needSadaratMale ? optionalUuidOrNull(data.sadaratMaleId) : null;
  payload.sadaratFemaleId = payload.needSadaratFemale
    ? optionalUuidOrNull(data.sadaratFemaleId)
    : null;
  const kh = typeof data.khidmatguzarItsNo === "string" ? data.khidmatguzarItsNo.trim() : "";
  payload.khidmatguzarItsNo = kh || null;
  const zak = typeof data.zakereenItsNo === "string" ? data.zakereenItsNo.trim() : "";
  payload.zakereenItsNo = zak || null;
  payload.address =
    typeof data.address === "string" && data.address.trim() ? data.address.trim() : null;
  payload.mobileNo =
    typeof data.mobileNo === "string" && data.mobileNo.trim() ? data.mobileNo.trim() : null;
  payload.notes = typeof data.notes === "string" && data.notes.trim() ? data.notes.trim() : null;
  return payload;
}

/** Drop legacy / server-managed fields from Ohbat PUT body; apply needSadarat rules. */
export function stripReadOnlyOhbatEventFields(
  data: Record<string, unknown>
): Record<string, unknown> {
  if (!data || typeof data !== "object") return data;
  const rest = { ...data };
  delete rest.itsNo;
  delete rest.googleCalendarEventId;
  delete rest.sadarat;
  delete rest.sadaratMale;
  delete rest.sadaratFemale;
  return applyNeedSadaratToPayload(rest);
}

/** POST body for raza-backed event create (Edit-in-Dialog / workspace). */
export function buildRazaBackedEventCreatePayload(
  data: Record<string, unknown>,
  razaRequestId: string
): Record<string, unknown> {
  const date = toApiDateOnly(data.date);
  const payload: Record<string, unknown> = {
    razaRequestId,
    date,
    startTime: String(data.startTime ?? "09:00").trim(),
  };
  const pur = typeof data.purpose === "string" && data.purpose.trim() ? data.purpose.trim() : "";
  if (pur) payload.purpose = pur;
  const hit = typeof data.hostItsNo === "string" ? data.hostItsNo.trim() : "";
  if (hit) payload.hostItsNo = hit;
  const hn = typeof data.hostName === "string" ? data.hostName.trim() : "";
  if (hn) payload.hostName = hn;
  payload.needSadaratMale = data.needSadaratMale !== false;
  payload.needSadaratFemale = data.needSadaratFemale === true;
  payload.sadaratMaleId = payload.needSadaratMale ? optionalUuidOrNull(data.sadaratMaleId) : null;
  payload.sadaratFemaleId = payload.needSadaratFemale
    ? optionalUuidOrNull(data.sadaratFemaleId)
    : null;
  const zak = typeof data.zakereenItsNo === "string" ? data.zakereenItsNo.trim() : "";
  payload.zakereenItsNo = zak || null;
  payload.address =
    typeof data.address === "string" && data.address.trim() ? data.address.trim() : null;
  payload.mobileNo =
    typeof data.mobileNo === "string" && data.mobileNo.trim() ? data.mobileNo.trim() : null;
  payload.title = typeof data.title === "string" && data.title.trim() ? data.title.trim() : null;
  payload.notes = typeof data.notes === "string" && data.notes.trim() ? data.notes.trim() : null;
  return payload;
}
